/**
 * BW20-B — per-meal kcal + macro range targeting.
 *
 * Lifesum's per-meal range is fixed by user preference. Haus's per-meal range
 * is computed live from training schedule + recovery state + GLP-1 dose
 * timing, so the range itself is the daily prescription, not a static budget.
 *
 * Pure function. No I/O. Caller is responsible for fetching the seed
 * meal_type_target rows and the day's training_session / recovery / GLP-1
 * inputs and passing them in.
 *
 * Behaviors:
 *   - Recovery red: drop pre_workout slot, expand dinner band by +15% kcal.
 *   - Recovery green/amber + scheduled workout: insert pre_workout (15-25%
 *     of daily, carbs-heavy, ~2h before) + post_workout (15-25% of daily,
 *     protein + carbs, <=60 min after). Neighboring meals lose proportional
 *     kcal share so the daily budget still sums correctly.
 *   - GLP-1 dose day-of-week 0-1 (week 1 after injection): narrow each
 *     meal's kcal_max by 15% (appetite suppression window).
 */

export type MealType =
  | "breakfast"
  | "snack_morning"
  | "lunch"
  | "snack_afternoon"
  | "dinner"
  | "snack_evening"
  | "pre_workout"
  | "post_workout";

export type RecoveryState = "green" | "amber" | "red";

export type DietPattern = "plant_forward" | "omnivore" | "pescatarian";

export type TrainingSession = {
  starts_at: Date;
  // Session duration in minutes; used to scope post-workout window.
  duration_min: number;
  // Estimated kcal expenditure. Used to bias post_workout kcal up if the
  // session was very long (>= 90 min) or very intense.
  estimated_kcal?: number;
  // 'low' | 'moderate' | 'high'. Drives whether pre_workout slot is
  // inserted at all (low can be skipped).
  intensity: "low" | "moderate" | "high";
};

export type MealTypeTarget = {
  meal_type: MealType;
  kcal_min: number;
  kcal_max: number;
  carbs_min_g?: number;
  carbs_max_g?: number;
  protein_min_g?: number;
  protein_max_g?: number;
  fat_min_g?: number;
  fat_max_g?: number;
};

export type MealTypeTargetMap = Partial<Record<MealType, MealTypeTarget>>;

export type ComputeMealTypeTargetsArgs = {
  dailyKcalTarget: number;
  dietPattern: DietPattern;
  bodyMassKg: number;
  recoveryState: RecoveryState;
  trainingSessionToday?: TrainingSession;
  // 0-6 — days since last GLP-1 injection. 0 = injection day, 1 = day after.
  // Range 0-1 is the appetite-suppression window.
  glp1DoseDayOfWeek?: number;
};

const DEFAULT_SHARES: Record<
  Exclude<MealType, "pre_workout" | "post_workout">,
  { share: number; band: number }
> = {
  breakfast: { share: 0.22, band: 0.15 },
  snack_morning: { share: 0.08, band: 0.25 },
  lunch: { share: 0.28, band: 0.15 },
  snack_afternoon: { share: 0.08, band: 0.25 },
  dinner: { share: 0.26, band: 0.15 },
  snack_evening: { share: 0.08, band: 0.25 },
};

function rangeFromShare(
  total: number,
  share: number,
  band: number,
): { kcal_min: number; kcal_max: number } {
  const center = total * share;
  return {
    kcal_min: Math.round(center * (1 - band)),
    kcal_max: Math.round(center * (1 + band)),
  };
}

export function computeMealTypeTargets(
  args: ComputeMealTypeTargetsArgs,
): MealTypeTargetMap {
  const {
    dailyKcalTarget,
    bodyMassKg,
    recoveryState,
    trainingSessionToday,
    glp1DoseDayOfWeek,
  } = args;

  const out: MealTypeTargetMap = {};

  // Step 1: seed the six default slots.
  for (const [mealType, { share, band }] of Object.entries(DEFAULT_SHARES) as Array<
    [keyof typeof DEFAULT_SHARES, { share: number; band: number }]
  >) {
    const { kcal_min, kcal_max } = rangeFromShare(dailyKcalTarget, share, band);
    out[mealType] = {
      meal_type: mealType,
      kcal_min,
      kcal_max,
    };
  }

  // Step 2: training adjustments. A session today with at least moderate
  // intensity carves out pre + post workout slots from neighboring meals.
  // On red recovery, the pre_workout slot is suppressed and the kcal goes
  // back into dinner instead.
  const insertWorkoutSlots =
    Boolean(trainingSessionToday) &&
    trainingSessionToday!.intensity !== "low" &&
    recoveryState !== "red";

  if (insertWorkoutSlots && trainingSessionToday) {
    const sessionLong = trainingSessionToday.duration_min >= 90;
    const preShare = sessionLong ? 0.18 : 0.15;
    const postShare = sessionLong ? 0.22 : 0.18;

    const preCenter = dailyKcalTarget * preShare;
    out.pre_workout = {
      meal_type: "pre_workout",
      kcal_min: Math.round(preCenter * 0.85),
      kcal_max: Math.round(preCenter * 1.15),
      carbs_min_g: Math.round((preCenter * 0.65) / 4 * 0.9),
      carbs_max_g: Math.round((preCenter * 0.7) / 4 * 1.1),
      fat_min_g: 3,
      fat_max_g: Math.round((preCenter * 0.15) / 9),
    };

    const postCenter = dailyKcalTarget * postShare;
    out.post_workout = {
      meal_type: "post_workout",
      kcal_min: Math.round(postCenter * 0.85),
      kcal_max: Math.round(postCenter * 1.15),
      protein_min_g: Math.round(0.3 * bodyMassKg),
      protein_max_g: Math.round(0.4 * bodyMassKg),
      carbs_min_g: Math.round(0.7 * bodyMassKg),
      carbs_max_g: Math.round(1.0 * bodyMassKg),
    };

    // Carve kcal from neighboring meals so daily budget still sums right.
    // pre_workout takes from breakfast or snack_morning (whichever is closer
    // to the session). post_workout takes from snack_afternoon or lunch.
    // Simple approach: pull a flat ~15% off snack slots first, then ~10%
    // off the closest main meal.
    const carveTotal = (preShare + postShare) * dailyKcalTarget;
    const sources: Array<{ meal: MealType; weight: number }> = [
      { meal: "snack_morning", weight: 0.25 },
      { meal: "snack_afternoon", weight: 0.25 },
      { meal: "breakfast", weight: 0.15 },
      { meal: "lunch", weight: 0.2 },
      { meal: "snack_evening", weight: 0.15 },
    ];
    for (const { meal, weight } of sources) {
      const t = out[meal];
      if (!t) continue;
      const carve = carveTotal * weight;
      t.kcal_min = Math.max(0, Math.round(t.kcal_min - carve * 0.9));
      t.kcal_max = Math.max(t.kcal_min, Math.round(t.kcal_max - carve * 1.1));
    }
  }

  // Step 3: red recovery — drop pre_workout, redirect +15% of daily kcal
  // into dinner (recovery dinner: iron + complex carbs bias). Even if no
  // workout was scheduled, dinner expands so the shopping plan tilts toward
  // a recovery meal.
  if (recoveryState === "red") {
    delete out.pre_workout;
    const dinner = out.dinner;
    if (dinner) {
      const bonus = dailyKcalTarget * 0.15;
      dinner.kcal_min = Math.round(dinner.kcal_min + bonus * 0.85);
      dinner.kcal_max = Math.round(dinner.kcal_max + bonus * 1.15);
    }
  }

  // Step 4: GLP-1 week-1 narrowing. Ranges shrink so the per-meal max is
  // 15% lower than usual — appetite tolerance is reduced and we want to
  // protect against the user trying to hit yesterday's max and feeling sick.
  if (glp1DoseDayOfWeek != null && glp1DoseDayOfWeek <= 1) {
    for (const key of Object.keys(out) as MealType[]) {
      const t = out[key];
      if (!t) continue;
      t.kcal_max = Math.round(t.kcal_max * 0.85);
      // Make sure max stays >= min.
      if (t.kcal_max < t.kcal_min) t.kcal_min = t.kcal_max;
    }
  }

  return out;
}

/**
 * State / Meaning / Next-move copy generator for a per-meal slot card.
 * Adherence-neutral: never says "you failed", "missed", "should have".
 */
export function mealSlotCopy(args: {
  mealType: MealType;
  target: MealTypeTarget;
  loggedKcal: number;
}): { state: string; meaning: string; nextMove: string } {
  const { mealType, target, loggedKcal } = args;
  const { kcal_min, kcal_max } = target;
  const below = loggedKcal < kcal_min;
  const above = loggedKcal > kcal_max;
  const within = !below && !above;

  const niceLabel = mealType.replace("_", " ");

  if (within) {
    return {
      state: `Logged ${Math.round(loggedKcal)} kcal at ${niceLabel}.`,
      meaning: `Inside the ${kcal_min} to ${kcal_max} range for this slot.`,
      nextMove: `On track. No action needed.`,
    };
  }
  if (below) {
    const gap = Math.round(kcal_min - loggedKcal);
    return {
      state: `Logged ${Math.round(loggedKcal)} kcal at ${niceLabel}. Range ${kcal_min} to ${kcal_max}.`,
      meaning: `${gap} kcal under the lower bound.`,
      nextMove:
        gap < 80
          ? `A piece of fruit or a slice of toast with nut butter closes the gap.`
          : `Add a small bowl of oats with nuts, or a sandwich with hummus.`,
    };
  }
  // above
  const over = Math.round(loggedKcal - kcal_max);
  return {
    state: `Logged ${Math.round(loggedKcal)} kcal at ${niceLabel}. Range ${kcal_min} to ${kcal_max}.`,
    meaning: `${over} kcal over the upper bound.`,
    nextMove: `The next meal range can absorb this. Consider a lighter dinner if today's training was easy.`,
  };
}
